<template>
  <div class="origin-home">
    <!-- 外派数据 -->
    <camp-data-board type="origin" :pbi="pbi" :iep="iep" @open="openStat"></camp-data-board>
    <div class="section-error" v-if="overviewError">{{ overviewError }}</div>
    <!-- 外派学员 -->
    <div class="camp-card">
      <div class="card-title">外派学员<span>（{{ studentTotal }}人）</span></div>
      <div class="section-error" v-if="studentsError">{{ studentsError }}</div>
      <div class="students-state" v-if="!studentsLoading && !students.length && !studentsError">暂无外派学员</div>
      <camp-person-card
        v-for="item in students"
        :key="item.id"
        :item="item"
        @open="goArchive(item)"
      ></camp-person-card>
    </div>
    <!-- 研修营列表 -->
    <div class="camp-card">
      <div class="card-title">研修营列表<span>（{{ camps.length }}个）</span></div>
      <div class="section-error" v-if="campsError">{{ campsError }}</div>
      <div class="camp-item" v-for="item in camps" :key="item.id" @click="goDetail(item)">
        <img class="camp-avatar" src="@/assets/images/researchCamp/banner.png" alt="">
        <div class="camp-main">
          <div class="camp-name">{{ item.name }}</div>
          <div class="camp-meta">主任：{{ item.director }} {{ item.region }} 我的学员{{ item.count }}人</div>
        </div>
        <img class="camp-arrow" src="@/assets/images/person/right.png" alt="">
      </div>
    </div>
    <!-- 考试数据 -->
    <camp-exam-data-popup v-model="examVisible" :statistics="iep"></camp-exam-data-popup>
  </div>
</template>

<script>
import CampDataBoard from './CampDataBoard.vue'
import CampPersonCard from './CampPersonCard.vue'
import CampExamDataPopup from './CampExamDataPopup.vue'
import { formatCampDate } from '../date'
import {
  getResearchCampDetail,
  getResearchCampStaffOverview,
  getResearchCampStaffHomeCamps,
  getResearchCampStaffHomeLearners,
  getResearchCampStaffHomeLearnersAll
} from '@/api/researchCamp'

export default {
  name: 'CampOriginHome',
  components: {
    CampDataBoard,
    CampPersonCard,
    CampExamDataPopup
  },
  props: {
    campConfigId: {
      type: [Number, String],
      default: null
    },
    viewRole: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      examVisible: false,
      overviewError: '',
      studentsError: '',
      studentsLoading: false,
      studentTotal: 0,
      studentRequestSeq: 0,
      campsError: '',
      pbi: {},
      iep: {},
      students: [],
      camps: []
    }
  },
  created () {
    this.loadOverview()
    this.loadStudents()
    this.loadCamps()
  },
  methods: {
    // 加载外派数据概览
    async loadOverview () {
      if (!this.campConfigId) return
      this.overviewError = ''
      try {
        const response = await getResearchCampStaffOverview(this.campConfigId, { viewRole: this.viewRole })
        const home = response && response.data ? response.data : {}
        this.pbi = home.statistics || {}
        this.iep = home.iepStatistics || {}
      } catch (error) {
        this.overviewError = error && error.msg ? error.msg : '外派数据加载失败'
      }
    },
    // 加载外派学员
    async loadStudents (campConfigId) {
      const requestSeq = ++this.studentRequestSeq
      this.students = []
      this.studentTotal = 0
      this.studentsError = ''
      this.studentsLoading = true
      try {
        const response = await getResearchCampStaffHomeLearnersAll(this.viewRole, { campConfigId })
        if (requestSeq !== this.studentRequestSeq) return
        const rows = response && Array.isArray(response.data) ? response.data : []
        this.students = rows.map(item => ({
          id: item.campMemberId,
          campConfigId: item.campConfigId,
          name: item.name || '',
          level: item.currentIepLevel || item.entryIepLevel || '',
          center: item.campName || '',
          hospital: item.hospitalName || '',
          department: item.departmentL1 || '',
          title: item.positionTitle || '',
          joinDate: formatCampDate(item.entryTime),
          status: item.progressStatus === 'NORMAL' ? 'normal' : 'behind'
        }))
        this.studentTotal = rows.length
      } catch (error) {
        if (requestSeq !== this.studentRequestSeq) return
        this.studentsError = error && error.msg ? error.msg : '外派学员加载失败'
      } finally {
        if (requestSeq === this.studentRequestSeq) this.studentsLoading = false
      }
    },
    // 加载原中心关联的研修营
    async loadCamps () {
      this.campsError = ''
      try {
        const response = await getResearchCampStaffHomeCamps(this.viewRole)
        const data = response && response.data
        let camps
        if (Array.isArray(data)) {
          camps = data
        } else if (data == null) {
          camps = []
        } else if (Array.isArray(data.list)) {
          camps = data.list
        } else {
          const error = new Error('研修营列表响应格式异常')
          error.msg = error.message
          throw error
        }
        this.camps = await Promise.all(camps.filter(Boolean).map(camp => this.loadCampSummary(camp)))
      } catch (error) {
        this.campsError = error && (error.msg || error.message)
          ? (error.msg || error.message)
          : '研修营列表加载失败'
      }
    },
    // 补齐单个营的详情和学员数
    async loadCampSummary (camp) {
      const campConfigId = camp.campConfigId || camp.id
      const result = {
        id: campConfigId,
        name: camp.campName || '',
        director: camp.directorName || '-',
        region: [camp.provinceName, camp.cityName].filter(Boolean).join('') || '-',
        count: '-'
      }
      const [detailResponse, learnerResponse] = await Promise.all([
        getResearchCampDetail(campConfigId).catch(() => null),
        getResearchCampStaffHomeLearners(this.viewRole, { campConfigId, pageNum: 1, pageSize: 10 })
          .catch(() => null)
      ])
      if (detailResponse && detailResponse.data) {
        const detail = detailResponse.data
        result.name = detail.name || result.name
        result.region = [detail.provinceName, detail.cityName].filter(Boolean).join('') || '-'
      }
      if (learnerResponse && learnerResponse.data) {
        const page = learnerResponse.data
        result.count = page.total === undefined ? '-' : page.total
      }
      return result
    },
    // 外派学员、进度正常、未达标进入学员名单
    openStat (item) {
      if (item.key === 'exam') {
        this.examVisible = true
        return
      }
      if (item.path) {
        this.$router.push({
          path: item.path,
          query: Object.assign({}, item.query, {
            viewRole: this.viewRole
          })
        })
      }
    },
    // 进入学员档案
    goArchive (item) {
      this.$router.push({
        path: '/researchCamp/archive',
        query: { studentId: item.id, campConfigId: item.campConfigId, viewRole: this.viewRole }
      })
    },
    // 原中心进入营时只看自己派出的学员
    goDetail (item) {
      this.$router.push({ path: '/researchCamp/detail', query: { researchCampId: item.id } })
    }
  }
}
</script>

<style lang="less" scoped>
@import '../camp.less';

.origin-home {
  padding-bottom: 24px;

  .card-title {
    span {
      margin-left: 4px;
      font-size: 11px;
      font-weight: 400;
      color: @camp-sub;
    }
  }

  .students-state {
    padding: 18px 0;
    text-align: center;
    font-size: 12px;
    color: @camp-sub;
  }

  .camp-card {
    .camp-white-card();
    margin-top: 16px;
    padding: 16px 14px 4px;

    .card-title {
      font-size: 14px;
      font-weight: 600;
    }

    /deep/ .person-card {
      margin: 0;
      padding: 12px 0;
      background: transparent;
      border-radius: 0;

      & + .person-card {
        border-top: 1px solid @camp-line;
      }
    }

    .camp-item {
      display: flex;
      align-items: center;
      padding: 12px 0;

      & + .camp-item {
        border-top: 1px solid @camp-line;
      }
    }

    .camp-avatar {
      width: 40px;
      height: 40px;
      margin-right: 10px;
      border-radius: 50%;
      object-fit: cover;
    }

    .camp-main {
      flex: 1;
      min-width: 0;
    }

    .camp-name {
      font-size: 14px;
      line-height: 20px;
    }

    .camp-meta {
      font-size: 12px;
      line-height: 18px;
      color: @camp-sub;
    }

    .camp-arrow {
      width: 12px;
      margin-left: 8px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/components/CampOriginHome.vue